import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { UploadCloud, Download, Trash2, FileSpreadsheet, Clock } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CIRateCards() {
  const { token } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const fetchCards = () => {
    axios.get(API_URL + '/api/ci-rate-cards?authorization=' + token)
      .then((r) => setCards(r.data || []))
      .catch(() => toast.error('Failed to load rate cards'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCards(); }, [token]);

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    axios.post(API_URL + '/api/ci-rate-cards/upload?authorization=' + token, fd)
      .then(() => { toast.success('Rate card uploaded'); fetchCards(); })
      .catch((err) => toast.error(err.response?.data?.detail || 'Upload failed'))
      .finally(() => { setUploading(false); e.target.value = ''; });
  };

  const handleDelete = (id) => {
    axios.delete(API_URL + '/api/ci-rate-cards/' + id + '?authorization=' + token)
      .then(() => { toast.success('Rate card deleted'); fetchCards(); })
      .catch(() => toast.error('Failed to delete'));
  };

  const handleDownload = (rc) => {
    const a = document.createElement('a');
    a.href = API_URL + '/api/ci-rate-cards/' + rc.id + '/download?authorization=' + token;
    a.download = (rc.filename || 'rate_card') + '.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="ci-rate-cards-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">C&I Rate Cards</h1>
            <p className="text-slate-500 mt-1">Upload and manage the Excel rate cards used by the C&I Contract Generator and Automated Renewals</p>
          </div>
          <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="bg-[#015492] hover:bg-[#013f6e]" data-testid="rate-card-upload-btn">
            {uploading ? <Clock className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />}
            Upload Rate Card (.xlsx)
          </Button>
          <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload} data-testid="rate-card-file-input" />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Uploaded Rate Cards ({cards.length})</CardTitle>
            <CardDescription>These cards drive C&I pricing across contracts, quotes and renewals</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-slate-500 text-sm py-4">Loading...</p>
            ) : cards.length === 0 ? (
              <p className="text-slate-500 text-sm py-4">No rate cards uploaded yet.</p>
            ) : (
              <div className="space-y-2">
                {cards.map((rc) => (
                  <div key={rc.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`rate-card-row-${rc.id}`}>
                    <FileSpreadsheet className="w-5 h-5 text-green-600" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{rc.filename}</p>
                      <p className="text-xs text-slate-500">Uploaded by {rc.uploaded_by_name || 'Unknown'} • {rc.uploaded_at ? new Date(rc.uploaded_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</p>
                    </div>
                    {rc.data?.effective_date && <Badge variant="outline">{rc.data.effective_date}</Badge>}
                    <Button variant="ghost" size="sm" onClick={() => handleDownload(rc)} data-testid={`rate-card-download-${rc.id}`}><Download className="w-4 h-4" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" data-testid={`rate-card-delete-${rc.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete rate card?</AlertDialogTitle>
                          <AlertDialogDescription>"{rc.filename}" will be permanently removed. Contracts already generated are not affected.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(rc.id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
